import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from './AIMenu.module.scss';
import { useAIMenu } from './useAIMenu';
import { GhostSelectionOverlay } from './GhostSelectionOverlay';

export interface AIMenuHandle {
    toggle: () => void;
}

export interface AIMenuProps {
    /** Subject company org id, forwarded to the askAica RAG agent. */
    aiOrgId?: string;
}

function getCaretPosition(): { left: number; top: number } | null {
    const sel = document.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    // For a non-collapsed selection, anchor at the focus end (where the cursor
    // actually is). The whole-selection bounding rect can be far below the
    // viewport for a tall/multi-screen selection, which would push the fixed
    // panel off-screen.
    if (!sel.isCollapsed && sel.focusNode) {
        try {
            const focusRange = document.createRange();
            focusRange.setStart(sel.focusNode, sel.focusOffset);
            focusRange.collapse(true);
            const fRect = focusRange.getBoundingClientRect();
            if (fRect.height > 0) return { left: fRect.left, top: fRect.bottom + 4 };
        } catch {
            // fall through to the bounding-rect logic below
        }
    }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) {
        const collapsed = range.cloneRange();
        collapsed.collapse(true);
        const caretRect = collapsed.getBoundingClientRect();
        if (caretRect.height === 0) return null;
        return { left: caretRect.left, top: caretRect.bottom + 4 };
    }
    return { left: rect.left + rect.width / 2, top: rect.bottom + 4 };
}

// Keep a panel position reachable on-screen. `left` is the panel's CENTRE-x
// (the .menu transform is translateX(-50%)), so clamp against half the width.
function clampToViewport(left: number, top: number, halfWidth: number): { left: number; top: number } {
    const margin = 8;
    return {
        left: Math.min(Math.max(left, halfWidth + margin), window.innerWidth - halfWidth - margin),
        top: Math.min(Math.max(top, margin), window.innerHeight - 60),
    };
}

export const AIMenu = forwardRef<AIMenuHandle, AIMenuProps>(function AIMenu({ aiOrgId }, ref) {
    const {
        isOpen,
        close,
        toggle,
        prompt,
        setPrompt,
        streaming,
        error,
        result,
        hadSelection,
        savedSelection,
        submit,
        accept,
        insertBelow,
        discard,
        retry,
        abort,
    } = useAIMenu({ orgId: aiOrgId });
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
    const [dragging, setDragging] = useState(false);
    const dragOriginRef = useRef<{ px: number; py: number; left: number; top: number } | null>(null);

    useImperativeHandle(ref, () => ({ toggle }), [toggle]);

    useEffect(() => {
        if (isOpen) {
            const base = getCaretPosition() ?? { left: window.innerWidth / 2, top: window.innerHeight / 3 };
            const halfW = menuRef.current?.offsetWidth ? menuRef.current.offsetWidth / 2 : 210;
            setPos(clampToViewport(base.left, base.top, halfW));
            requestAnimationFrame(() => inputRef.current?.focus());
        } else {
            setPos(null);
        }
    }, [isOpen]);

    // Close on click outside. We deliberately do NOT render a full-screen
    // backdrop — one would sit above the editor's inner scroll container and
    // swallow wheel events, making the page unscrollable while the menu (and
    // its preview) is open. A document-level pointerdown listener gives the
    // same dismiss behavior without blocking scroll. Skip clicks on the AI
    // trigger button so re-clicking it toggles cleanly instead of racing the
    // close into a re-open. Attach on the next frame so the click that opened
    // the menu doesn't immediately close it.
    useEffect(() => {
        if (!isOpen) return undefined;
        const onPointerDown = (e: PointerEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            if (menuRef.current?.contains(target)) return;
            if (target.closest('[data-ai-trigger]')) return;
            close();
        };
        const raf = requestAnimationFrame(() => document.addEventListener('pointerdown', onPointerDown, true));
        return () => {
            cancelAnimationFrame(raf);
            document.removeEventListener('pointerdown', onPointerDown, true);
        };
    }, [isOpen, close]);

    // Drag-to-move. The grip starts a drag; window listeners track the pointer
    // so it keeps moving even if the cursor outruns the panel. pos.left is the
    // panel's CENTRE-x (the .menu transform is translateX(-50%)), so we clamp
    // against half the width to keep it reachable on-screen.
    useEffect(() => {
        if (!dragging) return undefined;
        const onMove = (e: PointerEvent) => {
            const o = dragOriginRef.current;
            if (!o) return;
            const el = menuRef.current;
            const halfW = el ? el.offsetWidth / 2 : 210;
            setPos(clampToViewport(o.left + (e.clientX - o.px), o.top + (e.clientY - o.py), halfW));
        };
        const onUp = () => {
            setDragging(false);
            dragOriginRef.current = null;
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, [dragging]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                if (streaming) abort();
                else close();
            } else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (streaming) return;
                // Once a result is previewed, Enter commits it; otherwise submit.
                if (result) accept();
                else submit();
            }
        },
        [streaming, abort, close, result, accept, submit],
    );

    const startDrag = useCallback(
        (e: ReactPointerEvent) => {
            if (!pos) return;
            e.preventDefault();
            dragOriginRef.current = { px: e.clientX, py: e.clientY, left: pos.left, top: pos.top };
            setDragging(true);
        },
        [pos],
    );

    if (!isOpen || !pos) return null;

    const showPreview = streaming || !!result;
    const showActions = !streaming && !!result;

    return (
        <>
            <GhostSelectionOverlay savedSelection={savedSelection} />
            {createPortal(
                <div
                    ref={menuRef}
                    className={dragging ? `${styles.menu} ${styles.menuDragging}` : styles.menu}
                    style={{ left: pos.left, top: pos.top }}
                >
                    <div className={styles.dragHandle} onPointerDown={startDrag} role="presentation" aria-hidden title="Drag to move">
                        <span className={styles.dragGrip} />
                    </div>
                    <div className={styles.inputRow}>
                        <span className={styles.sparkle} aria-hidden>
                            ✦
                        </span>
                        <textarea
                            ref={inputRef}
                            className={styles.input}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={hadSelection ? 'Tell AICA how to edit the selection…' : 'Ask AICA about this company…'}
                            disabled={streaming}
                            aria-label="AI prompt"
                            rows={3}
                        />
                        <button
                            type="button"
                            className={styles.submitBtn}
                            onClick={submit}
                            disabled={!prompt.trim() || streaming}
                            aria-label="Submit"
                            title="Submit (Enter · Shift+Enter for newline)"
                        >
                            ↵
                        </button>
                    </div>

                    {showPreview && (
                        <div className={styles.preview} aria-live="polite">
                            {result ? (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{result}</ReactMarkdown>
                            ) : (
                                <span className={styles.previewPlaceholder}>…</span>
                            )}
                        </div>
                    )}

                    {streaming && (
                        <div className={styles.statusRow}>
                            <span className={styles.spinner} aria-hidden />
                            <span>Generating…</span>
                            <button type="button" className={styles.stopBtn} onClick={abort}>
                                Stop
                            </button>
                        </div>
                    )}

                    {showActions && (
                        <div className={styles.actions}>
                            <button type="button" className={styles.acceptBtn} onClick={accept}>
                                {hadSelection ? 'Replace selection' : 'Insert'}
                            </button>
                            {hadSelection && (
                                <button type="button" className={styles.actionBtn} onClick={insertBelow}>
                                    Insert below
                                </button>
                            )}
                            <button type="button" className={styles.actionBtn} onClick={retry}>
                                Try again
                            </button>
                            <button type="button" className={styles.discardBtn} onClick={discard}>
                                Discard
                            </button>
                        </div>
                    )}

                    {error && (
                        <div className={styles.errorRow}>
                            <span>⚠</span>
                            <span>{error}</span>
                        </div>
                    )}

                    {!streaming && !result && !error && <div className={styles.hint}>⌘J to toggle · Enter to submit · Esc to close</div>}
                </div>,
                document.body,
            )}
        </>
    );
});
